import React, { useState, useMemo, useEffect, useRef } from "react";
import { IconX, IconPlus, IconTrash } from "@tabler/icons-react";
import Filters from "components/layout/filters";
import Sorters from "components/layout/sorters";
import { useSharedState } from "state";
import { TaskFilters, TaskFiltersDefault } from "types/task";

export default function PageLayout({
  children,
  header,
  subheaderText,
  search,
  filters,
  sorters = true,
  buttonText,
  buttonAction,
  additionalButton,
}: {
  children: React.JSX.Element;
  header?: string | React.JSX.Element;
  subheaderText?: string;
  search: boolean;
  filters: boolean;
  sorters?: boolean;
  buttonText?: string;
  buttonAction?: () => void;
  additionalButton?: React.JSX.Element;
}) {
  const {
    currentPageTab,
    resetPageTab,
    searchQuery,
    setSearchQuery,
    filtersList,
    setFiltersList,
    handleSearch,
    handleFilter,
    handleClearFilters,
    sortersList,
  } = useSharedState();

  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const prevMainPath = useRef("");

  useEffect(() => {
    // Get the main route path without sub-routes
    const currentMainPath = window.location.pathname.split("/")[1];

    if (prevMainPath.current !== currentMainPath) {
      (searchQuery || filtersCount || sortersList.length) && handleClearFilters();
      prevMainPath.current = currentMainPath;
    }

    if (currentPageTab !== "cards") resetPageTab();
  }, [window.location.pathname]);

  const filtersCount = useMemo(() => {
    if (!filtersList) return 0;
    return Object.entries(filtersList).filter(([_, value]) => value !== null && value !== undefined && value !== "")
      .length;
  }, [filtersList]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);

    timeoutId && clearTimeout(timeoutId);
    const newTimeoutId = window.setTimeout(() => {
      handleSearch(newSearchQuery);
    }, 1000);
    setTimeoutId(newTimeoutId);
  };

  const handleFilterChange = () => {
    // Create a new object by spreading TaskFiltersDefault
    const newFilterQuery: TaskFilters = { ...TaskFiltersDefault };
    Object.entries(filtersList).forEach(([key, value]) => {
      if (value) {
        newFilterQuery[key as keyof TaskFilters] = value;
      }
    });
    handleFilter(newFilterQuery);
  };

  const headerSearchInput = (
    <div className="flex-grow-1 flex-md-grow-0">
      <div className="input-group input-group-flat">
        <input
          className="form-control d-inline-block"
          type="search"
          placeholder="Поиск"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <span className="input-group-text">
          {searchQuery && (
            <div className="link-secondary cursor-pointer" onClick={() => handleSearch("")}>
              <IconX className="icon" />
            </div>
          )}
        </span>
      </div>
    </div>
  );

  const headerButtons = (filters || searchQuery || buttonText) && (
    <div className="d-flex flex-wrap gap-2 w-full md-w-fit">
      {filters && (
        <Filters
          filtersList={filtersList}
          setFiltersList={setFiltersList}
          onFiltersReset={handleClearFilters}
          onFiltersSubmit={handleFilterChange}
        />
      )}
      {sorters && <Sorters />}

      {filtersCount ? (
        <button className="btn btn-ghost-danger grow" onClick={handleClearFilters}>
          <IconTrash className="icon" />
          Очистить всё
        </button>
      ) : undefined}

      {buttonText && (
        <button className="btn btn-success grow" onClick={buttonAction}>
          <IconPlus className="icon" />
          {buttonText}
        </button>
      )}
    </div>
  );

  return (
    <div className="page-wrapper">
      {header && (
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row gap-2 align-items-center">
              <div className="col-auto">
                <h2 className="page-title d-block">{header}</h2>
                {subheaderText && <div className="text-muted mt-1">{subheaderText}</div>}
              </div>

              {(search || buttonText) && (
                <div className="d-flex flex-grow-1 flex-wrap justify-content-end gap-2 col-auto">
                  {additionalButton}

                  {search && headerSearchInput}

                  {headerButtons}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-body">
        <div className="container-xl">{children}</div>
      </div>
    </div>
  );
}
